import { FC, useContext } from 'react'
import { useTranslate } from 'react-polyglot'
import { Link, useHistory } from 'react-router-dom'
import { tokens } from '@getgo/chameleon-core/next/goto-admin'
import { CloseOutlinedIcon, InfoOutlinedIcon } from '@getgo/chameleon-icons/react'
import { Button, IconButton, Typography } from '@getgo/chameleon-web-react-wrapper'
import { Grid, makeStyles } from '@material-ui/core'
import { Analytics, BILLING_ROUTE, PRODUCTS_ROUTE } from '@ucc/unified-admin-common'

import { OrganizationContext } from 'components'
import { mixpanelEventNames } from 'utils'
import { RESOLVE_TRIAL_ROUTE } from 'utils/constants'
import { getProductFamilyName } from 'utils/getProductNames'
import {
  getSubscriptionRemainingDays,
  isFree,
  isFreeOrTrialNonAddonResolveSubscription,
  isTrial,
} from 'utils/subscriptionUtils'
import { useAccountContext } from './contexts/AccountContext'
import { AppThemeContext } from './providers/AppThemeProvider'

const useStyles = makeStyles(() => ({
  bannerContent: {
    position: 'sticky',
    zIndex: 1000,
    top: 0,
    alignItems: 'center',
    minHeight: '40px',
  },
  bannerLinks: {
    paddingRight: '65px',
  },
  bannerClose: {
    right: '10px',
    position: 'absolute',
  },
}))

export interface ResolveUpgradeBannerProps {
  handleHideBanner: () => void
}

const ResolveUpgradeBanner: FC<ResolveUpgradeBannerProps> = ({ handleHideBanner }) => {
  const t = useTranslate()
  const history = useHistory()
  const appTheme = useContext(AppThemeContext)
  const { accountKey: selectedAccountKey } = useAccountContext()
  const classes = useStyles()
  const organizationContext = useContext(OrganizationContext)
  const resolveName = getProductFamilyName('gotoresolve')

  const freeOrTrialSubscription = organizationContext!.subscriptions!.find(isFreeOrTrialNonAddonResolveSubscription)!

  const subscriptionsUrl = `/${selectedAccountKey}/${BILLING_ROUTE}/subscriptions/resolve/manage`
  const trialUrl = `/${selectedAccountKey}/${PRODUCTS_ROUTE}/${RESOLVE_TRIAL_ROUTE}`

  const trackUpgrade = (url: string) => {
    if (isTrial(freeOrTrialSubscription)) {
      Analytics.track(mixpanelEventNames.RESOLVE_CONVERSION, {
        Action: 'Buy cta clicked',
        selectedAccountKey,
        url,
        trialRemainingDays: getSubscriptionRemainingDays(freeOrTrialSubscription),
      })
    } else {
      Analytics.track(mixpanelEventNames.RESOLVE_CONVERSION, {
        Action: 'Buy cta clicked',
        selectedAccountKey,
        url,
      })
    }
  }

  const trackStartTrial = () => {
    Analytics.track(mixpanelEventNames.RESOLVE_CONVERSION, {
      Action: 'Trial cta clicked',
      selectedAccountKey,
    })
  }

  const handleStartTrial = () => {
    trackStartTrial()
    history.push(trialUrl)
  }

  return (
    <Grid
      direction="row"
      container
      spacing={2}
      justifyContent="center"
      alignItems="center"
      data-testid="resolve-upgrade-banner"
      className={classes.bannerContent}
      style={{ backgroundColor: appTheme.chameleonTheme === 'dark' ? tokens.yellow600 : '#F2DD06' }}
    >
      <Grid item>
        <InfoOutlinedIcon size="21px" />
      </Grid>
      <Grid item>
        <Typography variant="body-small" noWrap>
          {isTrial(freeOrTrialSubscription)
            ? t('Your free trial for %{resolveName} ends in %{expiringDays} days.', {
                resolveName: resolveName,
                expiringDays: getSubscriptionRemainingDays(freeOrTrialSubscription),
              })
            : t('You currently have a Free plan for %{resolveName}.', {
                resolveName: resolveName,
              })}
        </Typography>
      </Grid>
      <Grid className={classes.bannerLinks} item>
        <Link onClick={() => trackUpgrade(subscriptionsUrl)} style={{ textDecoration: 'none' }} to={subscriptionsUrl}>
          <Button variant="neutral">
            {isTrial(freeOrTrialSubscription) ? t('Upgrade your plan') : t('Upgrade now')}
          </Button>
        </Link>
      </Grid>
      <Grid item>
        {isFree(freeOrTrialSubscription) && (
          <Button onClick={handleStartTrial} variant="neutral">
            {t('Start a trial')}
          </Button>
        )}
      </Grid>
      <Grid className={classes.bannerClose} xs item>
        <IconButton variant="secondary" data-testid="resolve-banner-close-button" onClick={handleHideBanner}>
          <CloseOutlinedIcon size="21px" />
        </IconButton>
      </Grid>
    </Grid>
  )
}

export default ResolveUpgradeBanner
